import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DEFAULT_ACCESS_LEVEL_OPTIONS, filterAccessOptions } from 'src/app/shared/utils/access-level.util';
import { IAdminAndUserAuthResponse, IBMTAccessLevels, ISubGroupAccessLevels } from 'src/app/store/createManageUser/state';

export function syncNestedUserAuthControl(
  editForm: UntypedFormGroup | null | undefined,
  adminControlName: string,
  userControlName: string,
  onValueSynced: () => void,
  requireTruthy = false
): void {
  const adminValue = editForm?.controls?.[adminControlName]?.value;
  if (requireTruthy && !adminValue) {
    return;
  }

  const userAuthForm = editForm?.controls?.userAuthForm as UntypedFormGroup | undefined;
  userAuthForm?.controls?.[userControlName]?.setValue(adminValue);
  onValueSynced();
}

export function refreshDirtyStateFromSnapshot(
  userAuthForm: UntypedFormGroup | undefined,
  updateDirtyState: (value: boolean) => void,
  initialSnapshot: string,
  isDelegate: boolean
): void {
  if (!userAuthForm) {
    updateDirtyState(false);
    return;
  }

  updateDirtyState(isDelegate || initialSnapshot !== JSON.stringify(userAuthForm.getRawValue()));
}

export function isDelegateUser(selectedUserType: string | undefined): boolean {
  return (selectedUserType || '').toLowerCase() === 'delegate';
}

export function resolveManagedUserType(selectedOption: string | undefined): 'Delegate' | 'User' {
  return selectedOption === 'delegate' ? 'Delegate' : 'User';
}

type AccessLevelField = 'eligibilityAccessLevel' | 'satAccessLevel';

export function withFallbackUserAuthorizations(
  authResponse: IAdminAndUserAuthResponse,
  fallbackUserAuthorizations: ISubGroupAccessLevels[]
): IAdminAndUserAuthResponse {
  return {
    ...authResponse,
    userRelatedAuthorizations: authResponse?.userRelatedAuthorizations?.length
      ? authResponse.userRelatedAuthorizations
      : fallbackUserAuthorizations
  };
}

export function resolveScopedAccessLevelOptions(
  authResponse: IAdminAndUserAuthResponse | undefined,
  accessLevelField: AccessLevelField
) {
  return filterAccessOptions(
    DEFAULT_ACCESS_LEVEL_OPTIONS,
    authResponse?.adminRelatedAuthorizations?.[accessLevelField]
  );
}

export function createAllGroupsAuthorizationForm(
  fb: UntypedFormBuilder,
  adminAuthorizations: IBMTAccessLevels,
  userAuthorizations: ISubGroupAccessLevels
): { editForm: UntypedFormGroup; initialSnapshot: string } {
  const editForm = fb.group({
    adminEligAccess: '',
    adminClientKnowledge: false,
    adminCustomClientReport: false,
    adminBillingReport: false,
    adminClientDetailReport: false,
    adminSatAccess: '',
    userAuthForm: fb.group(buildScopedUserAuthorizationControls(adminAuthorizations, userAuthorizations))
  });

  const userAuthForm = editForm.controls.userAuthForm as UntypedFormGroup;
  editForm.controls.adminEligAccess.setValue(editForm.controls.adminEligAccess.value, { emitEvent: false });

  return {
    editForm,
    initialSnapshot: JSON.stringify(userAuthForm.getRawValue())
  };
}

function buildScopedUserAuthorizationControls(
  adminAuthorizations: IBMTAccessLevels,
  userAuthorizations: ISubGroupAccessLevels
): Record<string, string | boolean> {
  return {
    ...adminAuthorizations.eligibilityAccessLevel && { userEligAccess: userAuthorizations.eligibilityAccessLevel || 'None' },
    ...adminAuthorizations.clientKnowledgeIndicator && { userClientKnowledge: userAuthorizations.clientKnowledgeIndicator || false },
    ...adminAuthorizations.customClientReportAccessIndicator && { userCustomClientReportAccess: userAuthorizations.customClientReportAccessIndicator || false },
    ...adminAuthorizations.billingReportAccessIndicator && { userBillingReport: userAuthorizations.billingReportAccessIndicator || false },
    ...adminAuthorizations.claimDetailReportAccessIndicator && { userClientDetailReport: userAuthorizations.claimDetailReportAccessIndicator || false },
    ...adminAuthorizations.satAccessLevel && { userSatAccess: userAuthorizations.satAccessLevel || 'None' }
  };
}
